<template>
    <div>
        <!-- <Loader :field="'companies'" v-if="loadingScoutedJobs || listLoading" /> -->
        <div class="job-list">
            <!-- Scouted Jobs Section (API called only when section is expanded) -->
            <div>
                <div class="scouted-tickets-wrapper">
                    <div class="job-section scouted-tickets-section">
                        <div class="scouted-tickets-box">
                            <div
                                class="scouted-tickets-header d-flex justify-content-between align-items-center"
                                :class="{ 'scouted-tickets-header-collapsed': scoutedJobsCollapsed }"
                                data-testid="scouted-tickets-toggle"
                                @click="toggleScoutedSection"
                            >
                                <div class="scouted-tickets-header-left d-flex align-items-center">
                                    <img
                                        src="@/assets/images/ticket.svg"
                                        alt=""
                                        class="scouted-tickets-icon"
                                        width="24"
                                        height="24"
                                    />
                                    <span class="scouted-tickets-label">
                                        スカウト希望チケット
                                        <span v-if="remainingScoutTickets !== null">：残り{{ remainingScoutTickets }}枚</span>
                                    </span>
                                </div>
                                <i
                                    class="fas scout-tickets-chevron"
                                    :class="scoutedJobsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up'"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                        <button
                            v-if="scoutedJobsCollapsed"
                            type="button"
                            class="scouted-tickets-help-outside d-flex align-items-center"
                            data-testid="scout-help-text"
                            @click="openScoutHelpPopup"
                        >
                            <i class="far fa-question-circle scouted-tickets-help-icon" aria-hidden="true" />
                            <span class="scouted-tickets-help-label">スカウト希望とは</span>
                        </button>
                    </div>
                </div>
                <div v-show="!scoutedJobsCollapsed" class="scouted-tickets-content">
                        <Loader v-if="loadingScoutedJobs" :field="'scouted-jobs'" />
                    <div v-else class="section-content">
                        <JobListItem
                            v-for="(job, index) in filteredScoutedJobs"
                            :key="`scouted-${job._id}`"
                            :job="job"
                            :isJobScouted="true"
                            :isJobScoutActive="isJobScoutActive(job)"
                            :index="index"
                            :searchHighlights="job.highlights || []"
                            @click="getJobsToScout(job, index, 'scouted')"
                        />
                    </div>
                </div>
            </div>

            <!-- Other Jobs Section -->
            <div class="job-section other-jobs">
                <div class="section-content">
                    <h2 class="job-search-section-title">求人を探す</h2>
                    <div class="search-suggestions-wrapper">
                        <div class="search-row-single">
                            <div class="search-input-wrapper">
                                <i class="fa fa-search search-icon" aria-hidden="true" />
                                <input
                                    type="text"
                                    class="search-input"
                                    v-model="localSearchKeyword"
                                    placeholder="職種、企業名（複数ワード検索可）"
                                    data-testid="job-search-input"
                                    @keypress.enter="handleSearch"
                                    @blur="scheduleHideSuggestions"
                                    @focus="showSuggestionsIfHasResults"
                                />
                            </div>
                            <button
                                type="button"
                                class="search-settings-btn"
                                :class="{ 'search-settings-btn--active': isCompanyFilterActive }"
                                aria-label="求人表示する企業設定"
                                data-testid="job-search-settings-btn"
                                @click="openCompanyDisplaySettings"
                            >
                                <i class="fas fa-sliders-h" aria-hidden="true" />
                            </button>
                        </div>
                        <div
                            v-if="showSuggestions && (suggestionsList.length > 0 || suggestionsLoading)"
                            class="suggestions-dropdown"
                            data-testid="suggestions-dropdown"
                        >
                            <div v-if="suggestionsLoading" class="suggestions-loading">読み込み中...</div>
                            <ul v-else class="suggestions-list">
                                <li
                                    v-for="(suggestion, index) in suggestionsList"
                                    :key="index"
                                    class="suggestion-item"
                                    data-testid="suggestion-item"
                                    @mousedown.prevent="selectSuggestion(suggestion)"
                                >
                                    {{ suggestion }}
                                </li>
                            </ul>
                        </div>
                    </div>
                    <p v-if="!isBrowsingAllJobs" class="job-results-summary">
                        <span class="job-results-matched-cta">経験に近い公開中の求人を見る</span>
                    </p>
                    <div v-if="jobsListLoading" class="job-list-loader-wrap" data-testid="job-list-loader">
                        <Loader :field="'job-list'" />
                    </div>
                    <template v-else>
                    <!-- Toggle component - commented out for now -->
                    <!-- <div class="toggle-container p-2 mb-2">
                        <span class="toggle-label ms-2">AIによるマッチ求人のみ表示</span>
                        <toggle-button
                            v-model="localShowMatchedJobs"
                            :sync="true"
                            :labels="false"
                            :width="50"
                            :height="24"
                            color="#2F80ED"
                        />
                    </div> -->

                    <p v-if="isBrowsingAllJobs && serverJobs.length > 0" class="job-results-summary">
                        {{ resultsRangeStart }}-{{ resultsRangeEnd }}件表示/全{{ totalJobsCount }}件
                    </p>

                    <div
                        v-if="showNoJobResults && !jobsListLoading && !listLoading"
                        class="job-search-empty"
                        data-testid="job-search-no-results"
                    >
                        <p>該当する求人が見つかりませんでした。</p>
                    </div>

                    <div
                        v-if="listLoading && isBrowsingAllJobs && !jobsListLoading"
                        class="job-list-loader-wrap job-list-loader-wrap--inline"
                        data-testid="job-list-page-loader"
                    >
                        <Loader :field="'job-list'" />
                    </div>

                    <template v-else>
                    <JobListItem
                        v-for="(job, index) in displayedOtherJobs"
                        :key="`other-${job._id}`"
                        :job="job"
                        :isJobScouted="isUbmJob(job) ? false : isJobScoutedById(job._id)"
                        :index="index"
                        :searchHighlights="job.highlights || []"
                        @click="getJobsToScout(job, index, 'other')"
                    />
                    </template>
                    
                    <!-- Pagination -->
                    <div class="pagination-area" v-if="totalPages > 1 && isBrowsingAllJobs">
                        <Pagination
                            :key="`jobs-page-${currentPage}`"
                            :total-page="totalPages"
                            :callback="handlePageChange"
                            :currentPage="currentPage"
                        />
                    </div>
                    </template>
                </div>
            </div>
        </div>

        <CompanyDisplaySettingsModal
            ref="companyDisplaySettingsModal"
            :companies="companies"
            :selected-company-ids="selectedCompanyIds"
            :registered-company-ids="registeredCompanyIds"
            @apply="handleCompanyDisplayApply"
        />

        <ScoutHelpPopup ref="scoutHelpPopup" />

        <CompanySelectionModal
            ref="companySelectionModal"
            :companyName="selectedJobCompany || ''"
            :companyId="selectedJob && (selectedJob.company_id || selectedJob.companyId) || ''"
            :companyDescription="(selectedCompany && selectedCompany.short_description) || ''"
            :loading="loading"
            @proceed="handleCompanySelectionProceed"
            @cancel="handleCompanySelectionCancel"
        />

        <TermsConditionsModal
            ref="termsConditionsModal"
            @agree="handleTermsAgree"
            @cancel="handleTermsCancel"
        />

        <JobDescriptionModal
            ref="jobModal"
            :currentJob="selectedJob"
            :currentIndex="actualJobIndex"
            :totalJobs="totalJobsCount"
            :isJobScouted="isSelectedJobScouted"
            :hasReachedScoutLimit="hasReachedScoutLimit"
            :hasCompanyActiveScout="hasCompanyActiveScout"
            :loading="loadingJobDetails"
            :company="profile.company_name"
            @scout="openScoutConfirmation"
            @prev="handlePrev"
            @next="handleNext"
        />
        <ScoutConfirmationModal
            ref="scoutConfirmModal"
            @confirm="checkSharedProfile"
            @cancel="handleScoutCancel"
            :loading="loading"
        />

        <!-- Success Modal after scout submission -->
        <BaseModal
            ref="scoutSuccessModal"
            :modal-id="'scoutSuccessModal'"
            :modal-text="'スカウト希望が完了しました！'"
            :button-text="'OK'"
            @button-click="handleScoutSuccessOk"
            @hidden="onScoutSuccessHidden"
        />
    </div>
</template>

<script>
import JobDescriptionModal from "./JobDescriptionModal.vue";
import ScoutConfirmationModal from "./ScoutConfirmationModal.vue";
import CompanyDisplaySettingsModal from "./CompanyDisplaySettingsModal.vue";
import ScoutHelpPopup from "./ScoutHelpPopup.vue";
import CompanySelectionModal from "./CompanySelectionModal.vue";
import TermsConditionsModal from "./TermsConditionsModal.vue";
import BaseModal from "./BaseModal.vue";
import JobListItem from "./JobListItem.vue";
import Loader from "../Loader.vue";
import Api from "../../config/api";
import Pagination from "../Pagination.vue";
import { mapGetters, mapActions, mapMutations } from "vuex";
export default {
    name: "JobLists",
    components: {
        JobDescriptionModal,
        ScoutConfirmationModal,
        CompanyDisplaySettingsModal,
        ScoutHelpPopup,
        CompanySelectionModal,
        TermsConditionsModal,
        BaseModal,
        JobListItem,
        Loader,
        Pagination,
    },
    props: {
        showMatchedJobs: {
            type: Boolean,
            required: true,
        },
        searchKeyword: {
            type: String,
            default: "",
        },
        companies: {
            type: Array,
            default: () => [],
        },
        initialCompanyId: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            localSearchKeyword: '',
            matchedJobs: [], // UBM recommendations — fetched once, not company-filtered
            filteredJobs: [],
            jobs: [],
            scoutedJobs: [],
            allJobs: [],
            loading: false,
            loadingScoutedJobs: false,
            selectedJob: {},
            jobsByCompany: [], // Jobs from the same company
            currentJobIndex: 0,
            loadingJobDetails: false,
            showScoutConfirmation: false,
            clientPage: 1,
            pageSize: 10,
            scoutedJobsCollapsed: true,
            scoutedJobsFetched: false,
            jobsListLoading: false,
            otherJobsCollapsed: false,
            selectedJobType: 'other', // 'scouted' or 'other'
            scoutFilter: 'all', // 'all', 'active', 'expired'
            selectedCompanyIds: [], // company IDs sent to API and used for client-side filter
            hasSearched: false, // true once a keyword search has been performed
            arrowPosition: 76, // Default arrow position
            companyArrowPosition: 76, // Default company arrow position
            suggestionsList: [],
            showSuggestions: false,
            suggestionsLoading: false,
            suggestionsDebounceTimer: null,
            suggestionsRequestId: 0,
            suggestionsHideTimer: null,
            suppressSuggestions: false, // true when user clicked suggestion, prevents re-fetching
            activeScoutCount: 0, // Track active scout count separately for immediate UI updates
            isScoutCountLoaded: false, // Track when scout count has been loaded
        };
    },
    computed: {
        ...mapGetters({
            profile: 'getProfile',
            serverJobs: 'jobs/getJobs',
            totalJobs: 'jobs/getTotalJobs',
            currentPage: 'jobs/getCurrentPage',
            totalPages: 'jobs/getTotalPages',
            listLoading: 'jobs/getLoading',
            jobsPageSize: 'jobs/getPageSize',
        }),
        isCompanyFilterActive() {
            return Array.isArray(this.selectedCompanyIds) && this.selectedCompanyIds.length > 0;
        },
        // True when keyword search or company filter is active — uses all-jds only (no UBM)
        isBrowsingAllJobs() {
            return this.hasSearched || this.isCompanyFilterActive;
        },
        matchedUbmJobs() {
            const scoutedJobIds = new Set(this.scoutedJobs.map((job) => job._id));
            return this.matchedJobs.filter((job) => !scoutedJobIds.has(job._id));
        },
        matchedUbmJobIds() {
            return new Set(this.matchedUbmJobs.map((job) => job._id));
        },
        // Add computed property for two-way binding
        localShowMatchedJobs: {
            get() {
                return this.showMatchedJobs;
            },
            set(value) {
                this.$emit('toggle-change', value);
            }
        },
        selectedJobCompany() {
            return this.selectedJob && this.selectedJob.companyName 
        },
        selectedCompany() {
            const companyId = this.selectedJob && this.selectedJob.company_id;
            if (!companyId || !this.companies || !this.companies.length) return null;
            return this.companies.find(c => c._id === companyId) || null;
        },
        registeredCompanyIds() {
            const ids = this.profile && this.profile.other_companies;
            return Array.isArray(ids) ? ids.map(id => String(id)) : [];
        },
        resultsRangeStart() {
            if (!this.isBrowsingAllJobs || this.totalJobs === 0) return 0;
            const pageSize = this.jobsPageSize || 20;
            return (this.currentPage - 1) * pageSize + 1;
        },
        resultsRangeEnd() {
            if (!this.isBrowsingAllJobs || this.totalJobs === 0) return 0;
            const start = this.resultsRangeStart;
            if (this.serverJobs.length > 0) {
                return start + this.serverJobs.length - 1;
            }
            const pageSize = this.jobsPageSize || 20;
            return Math.min(start + pageSize - 1, this.totalJobs);
        },
        otherJobs() {
            if (this.isBrowsingAllJobs) {
                return this.serverJobs.filter((job) => !this.matchedUbmJobIds.has(job._id));
            }
            return this.matchedUbmJobs;
        },
        displayedOtherJobs() {
            let jobs = [];
            if (this.isBrowsingAllJobs) {
                jobs = this.otherJobs;
            } else {
                const start = (this.clientPage - 1) * this.pageSize;
                const end = start + this.pageSize;
                jobs = this.otherJobs.slice(start, end);
            }
            
            // Merge scouted job data (including scoutedAt) if job is scouted
            return jobs.map(job => {
                const scoutedJob = this.scoutedJobs.find(sj => sj._id === job._id);
                if (scoutedJob) {
                    return {
                        ...job,
                        scoutedAt: scoutedJob.scoutedAt,
                        status: scoutedJob.status,
                        expiresAt: scoutedJob.expiresAt
                    };
                }
                return job;
            });
        },
        totalJobsCount() {
            if (this.selectedJobType === 'scouted') {
                return this.filteredScoutedJobs.length;
            }
            if (this.isBrowsingAllJobs) {
                return this.totalJobs;
            }
            return this.matchedUbmJobs.length;
        },
        showNoJobResults() {
            if (this.jobsListLoading || this.listLoading) return false;
            if (this.displayedOtherJobs.length > 0) return false;
            if (this.isBrowsingAllJobs) {
                return true;
            }
            return this.matchedUbmJobs.length === 0;
        },
        actualJobIndex() {
            if (this.selectedJobType === 'scouted') {
                return this.currentJobIndex;
            }
            if (!this.isBrowsingAllJobs) {
                return this.currentJobIndex;
            }
            return ((this.currentPage - 1) * (this.jobsPageSize || 20)) + this.currentJobIndex;
        },
        isCurrentJobScouted() {
            return this.selectedJobType === 'scouted';
        },
        isSelectedJobScouted() {
            const id = this.selectedJob && this.selectedJob._id;
            if (!id) return false;
            return this.scoutedJobs.some(j => j._id === id);
        },
        activeScoutedJobs() {
            return this.scoutedJobs.filter(job => job.status === 'active');
        },
        remainingScoutTickets() {
            // During initial load, don't display anything to avoid showing incorrect count
            if (!this.isScoutCountLoaded) return null;
            // Use activeScoutCount if available and > 0, otherwise use activeScoutedJobs.length
            const count = this.activeScoutCount > 0 ? this.activeScoutCount : this.activeScoutedJobs.length;
            return Math.max(0, 10 - count);
        },
        filteredScoutedJobs() {
            if (this.scoutFilter === 'all') {
                return this.scoutedJobs;
            } else if (this.scoutFilter === 'active') {
                return this.scoutedJobs.filter(job => job.status === 'active');
            } else if (this.scoutFilter === 'expired') {
                return this.scoutedJobs.filter(job => job.status === 'expired');
            }
            return this.scoutedJobs;
        },
        hasReachedScoutLimit() {
            if (this.activeScoutedJobs.length >= 10) return true;
            const companyId = this.selectedJob && (this.selectedJob.company_id || this.selectedJob.companyId);
            if (!companyId) return false;
            return this.activeScoutedJobs.some(j => (j.companyId || j.company_id) && String(j.companyId || j.company_id) === String(companyId));
        },
        hasCompanyActiveScout() {
            // Check if there's an active scout from the same company as the selected job
            const companyId = this.selectedJob && (this.selectedJob.company_id || this.selectedJob.companyId);
            if (!companyId || !this.activeScoutedJobs || !this.activeScoutedJobs.length) return false;
            return this.activeScoutedJobs.some(j => 
                (j.companyId || j.company_id) && 
                String(j.companyId || j.company_id) === String(companyId)
            );
        },
    },
    methods: {
        ...mapActions({
            loadJobs: 'jobs/loadJobs'
        }),
        ...mapMutations('jobs', {
            setPage: 'setPage',
        }),
        isUbmJob(job) {
            return this.matchedUbmJobIds.has(job._id);
        },
        syncShowMatchedJobs() {
            const shouldShowMatchedOnly = !this.isBrowsingAllJobs;
            if (this.showMatchedJobs !== shouldShowMatchedOnly) {
                this.$emit('toggle-change', shouldShowMatchedOnly);
            }
        },
        async loadBrowseJobs(page = 1) {
            const showFullListLoader = page === 1 && this.serverJobs.length === 0;
            try {
                if (showFullListLoader) {
                    this.jobsListLoading = true;
                }
                await this.loadJobs({
                    page,
                    searchKeyword: this.localSearchKeyword,
                    companyIds: this.selectedCompanyIds,
                });
            } finally {
                this.jobsListLoading = false;
            }
        },
        updateArrowPosition() {
            this.$nextTick(() => {
                if (this.$refs.scoutFilterSelect) {
                    const select = this.$refs.scoutFilterSelect;
                    const selectedOption = select.options[select.selectedIndex];
                    const text = selectedOption.text;
                    
                    // Create a temporary span to measure text width
                    const tempSpan = document.createElement('span');
                    tempSpan.style.visibility = 'hidden';
                    tempSpan.style.position = 'absolute';
                    tempSpan.style.fontSize = '14px';
                    tempSpan.style.fontFamily = window.getComputedStyle(select).fontFamily;
                    tempSpan.textContent = text;
                    document.body.appendChild(tempSpan);
                    
                    const textWidth = tempSpan.offsetWidth;
                    document.body.removeChild(tempSpan);
                    
                    // Position arrow after text with 8px spacing, plus left padding (18px)
                    this.arrowPosition = 18 + textWidth + 8;
                }
            });
        },
        updateCompanyArrowPosition() {
            this.$nextTick(() => {
                if (this.$refs.companyFilterSelect) {
                    const select = this.$refs.companyFilterSelect;
                    const selectedOption = select.options[select.selectedIndex];
                    const text = selectedOption.text;
                    
                    // Create a temporary span to measure text width
                    const tempSpan = document.createElement('span');
                    tempSpan.style.visibility = 'hidden';
                    tempSpan.style.position = 'absolute';
                    tempSpan.style.fontSize = '14px';
                    tempSpan.style.fontFamily = window.getComputedStyle(select).fontFamily;
                    tempSpan.textContent = text;
                    document.body.appendChild(tempSpan);
                    
                    const textWidth = tempSpan.offsetWidth;
                    document.body.removeChild(tempSpan);
                    
                    // Position arrow after text with 8px spacing, plus left padding (18px)
                    this.companyArrowPosition = 18 + textWidth + 8;
                }
            });
        },
        openCompanyDisplaySettings() {
            this.$refs.companyDisplaySettingsModal.show();
        },
        openScoutHelpPopup() {
            this.$refs.scoutHelpPopup.show();
        },
        async handleCompanyDisplayApply(selectedIds) {
            this.selectedCompanyIds = Array.isArray(selectedIds) ? [...selectedIds] : [];
            this.syncShowMatchedJobs();
            // Company filter switches to all-jds only; UBM matches are initial-load only
            if (this.isBrowsingAllJobs) {
                await this.loadBrowseJobs(1);
            }
        },
        scheduleHideSuggestions() {
            if (this.suggestionsHideTimer) clearTimeout(this.suggestionsHideTimer);
            this.suggestionsHideTimer = setTimeout(() => {
                this.showSuggestions = false;
                this.suggestionsHideTimer = null;
            }, 150);
        },
        showSuggestionsIfHasResults() {
            if (this.suggestionsList.length > 0 || this.suggestionsLoading) {
                this.showSuggestions = true;
            }
        },
        // Display Google-like suggestions while typing - NOT USING
        /*async fetchSuggestions() {
            const query = this.localSearchKeyword && this.localSearchKeyword.trim();
            if (!query) {
                this.suggestionsList = [];
                this.showSuggestions = false;
                return;
            }
            const requestId = ++this.suggestionsRequestId;
            this.suggestionsLoading = true;
            try {
                await this.$auth.getToken();
                const { data } = await this.axios({
                    method: 'get',
                    url: Api.url(Api.endpoints.SUGGESTIONS),
                    params: { q: query },
                });
                if (requestId !== this.suggestionsRequestId) return;
                this.suggestionsList = data.suggestions || [];
                this.showSuggestions = true;
            } catch (error) {
                console.error("Error fetching suggestions:", error);
                if (requestId !== this.suggestionsRequestId) return;
                this.suggestionsList = [];
            } finally {
                if (requestId === this.suggestionsRequestId) {
                    this.suggestionsLoading = false;
                }
            }
        },*/
        selectSuggestion(suggestion) {
            // Suppress suggestions triggered by keyword watcher when selection is in progress
            this.suppressSuggestions = true;
            if (this.suggestionsHideTimer) {
                clearTimeout(this.suggestionsHideTimer);
                this.suggestionsHideTimer = null;
            }
            if (this.suggestionsDebounceTimer) {
                clearTimeout(this.suggestionsDebounceTimer);
                this.suggestionsDebounceTimer = null;
            }
            // Abort any in-flight fetch by advancing the request counter
            this.suggestionsRequestId++;
            this.showSuggestions = false;
            this.suggestionsList = [];
            this.localSearchKeyword = suggestion;
            this.handleSearch();
            // Re-enable suggestions after a short delay (allows manual typing after selection)
            setTimeout(() => {
                this.suppressSuggestions = false;
            }, 1000);
        },
        arraysEqual(a, b) {
            if (a.length !== b.length) return false;
            return a.every((v, i) => v === b[i]);
        },
        async handleCompanyFilterChange() {
            if (this.isBrowsingAllJobs) {
                await this.loadBrowseJobs(1);
                this.$nextTick(() => this.updateCompanyArrowPosition());
            }
        },
        async handleSearch() {
            await this.$nextTick();
            await this.performServerSearch();
        },
        performClientSearch() {
            if (!this.localSearchKeyword.trim()) {
                // If search is empty, show all jobs
                this.filteredJobs = this.jobs;
                return;
            }
            const searchTerms = this.localSearchKeyword
                .toLowerCase()
                .split(/[,\s]+/)
                .filter((term) => term);

            // Filter jobs that match ALL search terms
            this.filteredJobs = this.jobs.filter((job) => {
                // Create a single searchable text from relevant job fields
                const searchableText = [
                    job.job_title,
                    job.job_description,
                    job.location,
                ]
                    .join(" ")
                    .toLowerCase();

                // Job must match ALL search terms to be included
                return searchTerms.every((term) =>
                    searchableText.includes(term)
                );
            });
        },
        async handlePageChange(page) {
            this.scrollToTop();
            if (this.isBrowsingAllJobs) {
                await this.loadBrowseJobs(page);
            } else {
                this.clientPage = page;
            }
        },
        async performServerSearch() {
            try {
                this.hasSearched = true;
                this.syncShowMatchedJobs();
                await this.loadBrowseJobs(1);
            } finally {
                this.showSuggestions = false;
            }
        },
        getJobsToScout(job, index, jobType) {
            // Find the index of the clicked job in the appropriate list
            this.currentJobIndex = index;
            this.selectedJobType = jobType;
            if (this.currentJobIndex === -1) this.currentJobIndex = 0;

            // Set the selected job
            this.selectedJob = job;

            // Show the modal
            this.$refs.jobModal.show();
        },
        async handlePrev() {
            let currentJobs = this.selectedJobType === 'scouted' ? this.scoutedJobs : this.displayedOtherJobs;
            
            // If there are previous jobs in current section, move to previous
            if (this.currentJobIndex > 0) {
                this.currentJobIndex = this.currentJobIndex - 1;
                this.selectedJob = currentJobs[this.currentJobIndex];
                return;
            }
            
            // Handle beginning of current section based on job type
            switch (this.selectedJobType) {
                case 'scouted':;
                    this.$refs.jobModal.hide();
                    break;
                    
                case 'other':
                    if (this.isBrowsingAllJobs && this.currentPage > 1) {
                        const prevPage = this.currentPage - 1;
                        await this.loadBrowseJobs(prevPage);
                        this.currentJobIndex = this.displayedOtherJobs.length - 1;
                        this.selectedJob = this.displayedOtherJobs[this.currentJobIndex];
                        this.scrollToTop();
                    } else {
                        // Close modal for matched jobs or when no previous pages
                        this.$refs.jobModal.hide();
                    }
                    break;
                    
                default:
                    this.$refs.jobModal.hide();
            }
        },
        async handleNext() {
            let currentJobs = this.selectedJobType === 'scouted' ? this.scoutedJobs : this.displayedOtherJobs;
            
            
            // If there are more jobs in current section, move to next
            if (this.currentJobIndex < currentJobs.length - 1) {
                this.currentJobIndex = this.currentJobIndex + 1;
                this.selectedJob = currentJobs[this.currentJobIndex];
                return;
            }
            
            // Handle end of current section based on job type
            switch (this.selectedJobType) {
                case 'scouted':
                    this.$refs.jobModal.hide();
                    break;
                    
                case 'other':
                    if (this.isBrowsingAllJobs && this.currentPage < this.totalPages) {
                        const nextPage = this.currentPage + 1;
                        await this.loadBrowseJobs(nextPage);
                        this.currentJobIndex = 0;
                        this.selectedJob = this.displayedOtherJobs[this.currentJobIndex];
                        this.scrollToTop();
                    } else {
                        // Close modal for matched jobs or when no more pages
                        this.$refs.jobModal.hide();
                    }
                    break;
                    
                default:
                    this.$refs.jobModal.hide();
            }
        },
        openScoutConfirmation() {
            this.showScoutConfirmation = true;
            this.$nextTick(() => {
                this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.show();
            });
        },
        handleScoutCancel() {
            this.showScoutConfirmation = false;
            this.$nextTick(() => {
                this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.hide();
            });
        },
        handleCompanySelectionProceed() {
            // Proceed to terms modal
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.show();
            }
        },
        handleCompanySelectionCancel() {
            if (this.$refs.companySelectionModal) {
                this.$refs.companySelectionModal.hide();
            }
        },
        handleTermsAgree() {
            // Submit and close
            this.submitScoutRequest(this.selectedJob, this.selectedJob.company_id);
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.hide();
            }
        },
        handleTermsCancel() {
            if (this.$refs.termsConditionsModal) {
                this.$refs.termsConditionsModal.hide();
            }
        },
        checkSharedProfile(){
            const companyId = this.selectedJob && this.selectedJob.company_id;
            const hasSharedProfile = this.profile && this.profile.other_companies && this.profile.other_companies.includes(companyId);
            if (!hasSharedProfile) {
                this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.hide();
                this.showScoutConfirmation = false;
                if (this.$refs.companySelectionModal) {
                    this.$refs.companySelectionModal.show();
                }
            } else {
                this.handleScout();
            }
        },
        async handleScout() {
            try {
                this.loading = true;
                await this.submitScoutRequest(this.selectedJob);
                this.$nextTick(() => {
                    // this.$refs.scoutConfirmModal.hide();

                    this.$refs.scoutConfirmModal && this.$refs.scoutConfirmModal.hide();
                    this.showScoutConfirmation = false;
                    // this.handleNext();
                });
            } catch (error) {
                console.error("Scout request failed:", error);
                // Handle error (show error message, etc.)
            } finally {
                this.loading = false;
            }
        },
        async submitScoutRequest(jobId, companyToShare=null) {
            // Implement your API call here
            try {
                await this.$auth.getToken();
                const { _id, company_id } = jobId;
                const res = await this.axios({
                    method: "post",
                    url: Api.url(Api.endpoints.addToScout),
                    data: {
                        jobId: _id,
                        jobCompany: company_id,
                        companyToShare,
                    },
                });
                const scoutedJob = this.displayedOtherJobs.find(
                    (job) => job._id === jobId
                );
                const jobToPush = scoutedJob || this.selectedJob || null;
                if (
                    jobToPush &&
                    !this.scoutedJobs.some((job) => job._id === jobId)
                ) {
                    // Add scoutedAt from the API response
                    const jobWithScoutDate = {
                        ...jobToPush,
                        scoutedAt: res.data.data.scoutedAt,
                        status: res.data.data.status || 'active'
                    };
                    this.scoutedJobs.unshift(jobWithScoutDate);
                }
                // Update active scout count immediately for accurate ticket display
                await this.getActiveScoutCount();
                // Show success modal
                this.$refs.companySelectionModal && this.$refs.companySelectionModal.hide();
                this.$nextTick(() => {
                  this.$refs.scoutSuccessModal && this.$refs.scoutSuccessModal.show();
                });
            } catch (err) {
                console.log("Submitting scout request for job:", jobId);
            }
        },
        isJobScoutActive(job) {
            const scoutedJob = this.scoutedJobs.find(j => j._id === job._id);
            if (!scoutedJob) return false;
            // If job is active, it's scouted; otherwise return false (not scouted)
            return scoutedJob.status === 'active';
        },
        isJobScoutedById(jobId) {
            return this.scoutedJobs.some(job => job._id === jobId);
        },
        handleScoutSuccessOk() {
            if (this.$refs.scoutSuccessModal) {
                this.$refs.scoutSuccessModal.hide();
            }
        },
        onScoutSuccessHidden() {
            if (this.$refs.jobModal) {
                this.$refs.jobModal.hide();
            }
            this.scrollToTop();
        },
        async toggleScoutedSection() {
            this.scoutedJobsCollapsed = !this.scoutedJobsCollapsed;
        },
        async getUserBasedJobs() {
            try {
                this.jobsListLoading = true;
                await this.$auth.getToken();
                const jobs = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.allJobs),
                });
                this.matchedJobs = jobs.data;
                this.filteredJobs = jobs.data;
                this.jobs = jobs.data;
            } catch (err) {
                console.error("Error fetching jobs:", err);
            } finally {
                this.jobsListLoading = false;
            }
        },
        async getJobseekerScoutedJobs() {
            try {
                this.loadingScoutedJobs = true;
                await this.$auth.getToken();
                const jobs = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.scoutedJobs),
                });
                this.scoutedJobs = jobs.data;
                // Update active scout count when full list is loaded
                this.activeScoutCount = this.scoutedJobs.filter(job => job.status === 'active').length;
            } catch (err) {
                console.error("Error fetching jobs:", err);
            } finally {
                this.loadingScoutedJobs = false;
            }
        },
        async getActiveScoutCount() {
            try {
                await this.$auth.getToken();
                const { data } = await this.axios({
                    method: "get",
                    url: Api.url(Api.endpoints.activeScoutCount),
                });
                this.activeScoutCount = data.activeScoutCount || 0;
            } catch (err) {
                console.error("Error fetching active scout count:", err);
                // Fallback to 0 if API fails to prevent incorrect display
                this.activeScoutCount = 0;
            } finally {
                // Mark as loaded regardless of success or error
                this.isScoutCountLoaded = true;
            }
        },
        scrollToTop() {
            window.scrollTo(0, 0);
        },
    },
    async created() {
        await this.getUserBasedJobs();
        if (this.initialCompanyId) {
            this.selectedCompanyIds = [String(this.initialCompanyId)];
        }
        this.syncShowMatchedJobs();
        if (this.isBrowsingAllJobs) {
            await this.loadBrowseJobs(1);
        }
        // Fetch active scout count immediately for accurate ticket display
        await this.getActiveScoutCount();
        // Load scouted jobs in background
        this.scoutedJobsFetched = true;
        this.getJobseekerScoutedJobs();
        // When toggle is OFF, jobs will only load when user searches
        if (this.showMatchedJobs) {
            // Matched jobs are already loaded via getUserBasedJobs
        }
    },
    mounted() {
        this.updateArrowPosition();
        this.updateCompanyArrowPosition();
    },
    watch: {
        profile: {
            immediate: true,
            handler(profile) {
                // Filter logic removed - by default, show jobs from ALL companies
                // User must actively select companies via filter button to apply company filter
                if (!profile || !profile.other_companies || profile.other_companies.length === 0) return;
                // Initialize selectedCompanyIds as empty - filter is NOT active by default
                // this.selectedCompanyIds = profile.other_companies.map(id => String(id));
            },
        },
        showMatchedJobs: {
            handler(newVal) {
                // When toggle is turned OFF, don't load jobs automatically
                // Jobs will only load when user performs a search
                // if (!newVal) {
                //     this.performServerSearch();
                // }
            },
        },
        // Keep localSearchKeyword in sync with prop and notify parent on changes
        searchKeyword: {
            immediate: true,
            handler(newVal) {
                this.localSearchKeyword = newVal || '';
            }
        },
        localSearchKeyword(newVal) {
            if (newVal !== this.searchKeyword) {
                this.$emit('search', newVal);
            }
            // Display Google-like suggestions while typing - NOT USING
            /*if (this.suppressSuggestions) return;
            if (this.suggestionsDebounceTimer) clearTimeout(this.suggestionsDebounceTimer);
            this.suggestionsDebounceTimer = setTimeout(() => {
                this.fetchSuggestions();
                this.suggestionsDebounceTimer = null;
            }, 300);*/
        },
    },
    beforeDestroy() {
        if (this.suggestionsDebounceTimer) clearTimeout(this.suggestionsDebounceTimer);
        if (this.suggestionsHideTimer) clearTimeout(this.suggestionsHideTimer);
    },
};
</script>

<style scoped>
.jobs {
    justify-content: space-between;
}
.job-list {
    display: flex;
    flex-direction: column;
}

.job-section {
    margin-bottom: 20px;
}

.other-jobs {
    min-height: 44vh;
    height: auto;
}

.job-list-loader-wrap {
    min-height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.job-list-loader-wrap--inline {
    min-height: 120px;
}

.job-search-empty {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 28px 20px;
    margin: 20px 0 0;
    border-radius: 10px;
    color: #787878;
    font-size: 16px;
    text-align: center;
    background: #ffffff;
}

.job-search-empty p {
    margin: 0;
}

@media (max-width: 768px) {
    .job-search-empty {
        padding: 24px 16px;
        font-size: 14px;
    }
}

.section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 16px;
    background-color: #ffffff;
    /* border: 1px solid #e9ecef; */
    /* border-radius: 4px; */
    transition: background-color 0.2s ease;
}


.header-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.section-title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #000000;
}

.collapse-icon {
    font-size: 14px;
    color: black;
    transition: transform 0.2s ease;
}

.section-content {
    margin-top: 8px;
}

/* .job-card {
    background-color: white;
    border: 1px solid #e0e0e0;
    border-radius: 10px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    padding: 5px 50px;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    height: 60px;
}

.job-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
} */

/* .company-name {
    font-weight: bold;
    font-size: 16px;
    color: #3c3c3c;
    font-weight: 600;
    margin-bottom: 5px;
} */

.job-title {
    font-size: 14px;
    font-weight: 600;
    color: #3c3c3c;
}
.scouted-badge {
    margin: auto 0px;
}

.scouted-badge-text {
    width: 60px;
    border: 1px solid #fea403;
    color: #fea403;
    text-align: center;
    font-weight: bold;
    font-size: 10px;
}

/* Toggle container styles */
.toggle-container {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: #ffffff;
    font-size: 12px;
    margin: auto;
    width: 50%;
    border-radius: 8px;
}

.toggle-label {
    margin-right: 10px;
}

.job-search-section-title {
    font-size: 18px;
    font-weight: 600;
    color: #006C6B;
    margin-bottom: 12px;
}

.search-row-single {
    display: flex;
    align-items: center;
    background-color: #ffffff;
    border: 1px solid #ccc;
    border-radius: 10px;
    height: 60px;
    padding: 0 12px 0 16px;
    margin: 0 0 12px 0;
    width: 100%;
    position: relative;
}

.search-row-single .search-input-wrapper {
    flex: 1;
    margin-right: 8px;
}

.search-suggestions-wrapper {
    position: relative;
    overflow: visible;
}

.suggestions-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    z-index: 1000;
    max-height: 300px;
    overflow-y: auto;
    overflow-x: hidden;
    background-color: #ffffff;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    margin-top: 4px;
}

.suggestions-loading {
    padding: 12px 16px;
    font-size: 14px;
    color: #6c757d;
}

.suggestions-list {
    list-style: none;
    margin: 0;
    padding: 8px 0;
}

.suggestion-item {
    padding: 10px 16px;
    font-size: 14px;
    color: #212529;
    cursor: pointer;
}

.suggestion-item:hover {
    background-color: #f8f9fa;
}

.search-settings-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    padding: 0;
    border: none;
    background: transparent;
    color: #3c3c3c;
    cursor: pointer;
    border-radius: 6px;
}

.search-settings-btn:hover:not(:disabled) {
    background-color: #f0f0f0;
}

.search-settings-btn--active {
    outline: 2px solid #006C6B !important;
    outline-offset: 2px;
}

.search-settings-btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.search-settings-btn i {
    font-size: 20px;
}

.job-results-summary {
    font-size: 14px;
    color: #3c3c3c;
    margin-bottom: 12px;
}

.job-results-matched-cta {
    font-size: 12px;
    font-weight: bold;
    color: #006C6B;
}

.search-row {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
    background-color: #ffffff;
    border: 1px solid #ccc;
    border-radius: 10px;
    height: 87px;
    padding: 0 12px;
    margin: auto;
    width: 50%;
    position: relative;
}

.search-label {
    margin-right: 15px;
    min-width: 80px;
    white-space: nowrap;
    font-size: 12px;
}

.search-input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    margin-right: 10px;
    flex: 1;
    height: 100%;
}

.search-icon {
    position: absolute;
    left: 12px;
    color: #666;
    font-size: 20px;
    pointer-events: none;
    z-index: 1;
}

.search-input {
    padding: 0 12px 0 45px;
    border: none;
    border-radius: 0;
    background-color: transparent;
    font-size: 20px;
    width: 100%;
    height: 100%;
    outline: none;
}

.search-input::placeholder {
    font-size: 20px;
    color: #666;
}

.search-button {
    padding: 8px 12px;
    background-color: #4F46E5;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
    font-size: 20px;
    color: #fff;
    height: 60px;
    min-width: 133px;
    font-weight: bold;
}

.search-button:disabled {
    cursor: not-allowed;
    opacity: 0.7;
}

.search-disabled {
    opacity: 0.85;
    cursor: not-allowed;
}

.search-input:disabled {
    cursor: not-allowed;
}

.search-tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    padding: 8px 12px;
    border-radius: 4px;
    font-size: 12px;
    white-space: nowrap;
    z-index: 1000;
    pointer-events: none;
    opacity: 0;
    transition: none;
}

.search-row:hover .search-tooltip {
    opacity: 1;
}

.search-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #333;
}

.scouted-tickets-section {
    margin-top: 1.5rem;
}

.scouted-tickets-wrapper {
    width: 100%;
}

@media (min-width: 768px) {
    .scouted-tickets-wrapper {
        width: 50%;
        margin-left: auto;
        margin-right: auto;
    }

    .scouted-tickets-help-icon {
        font-size: 12.8px;
        width: 14px;
    }

    .scouted-tickets-help-label {
        font-size: 12px;
    }
}

.scouted-tickets-box {
    border: 2px solid #AEAEAE;
    border-radius: 0;
    background-color: #E7F6F6;
    overflow: hidden;
}

.scouted-tickets-header {
    padding: 12px 16px;
    cursor: pointer;
    background-color: #E7F6F6;
    border-bottom: 1px solid #e9ecef;
    transition: background-color 0.15s ease;
}

.scouted-tickets-header-collapsed {
    border-bottom-color: transparent;
}

.scouted-tickets-header:hover {
    background-color: #d4efef;
}

.scouted-tickets-header-left {
    gap: 10px;
}

.scouted-tickets-help-outside {
    display: inline-flex;
    gap: 6px;
    margin-top: 8px;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font: inherit;
}

.scouted-tickets-help-icon {
    font-size: 13px;
    width: 14px;
    display: inline-block;
    color: #515D6B;
}

.scouted-tickets-help-label {
    font-family: 'Roboto', sans-serif;
    font-size: 10px;
    font-weight: 700;
    font-style: normal;
    line-height: 1;
    letter-spacing: 0;
    color: #515D6B;
    text-decoration: underline;
}

.scouted-tickets-icon {
    flex-shrink: 0;
    display: block;
}

.scouted-tickets-label {
    font-size: 12px;
    color: #3c3c3c;
}

.scout-tickets-chevron {
    font-size: 14px;
    color: #3c3c3c;
    transition: transform 0.2s ease;
}

.scouted-tickets-content {
    padding: 0;
    margin-top: 8px;
}

.scout-filter-row {
    border-bottom: 1px solid #e9ecef;
}

.scouted-header {
    border-bottom: 1px solid #e9ecef;
}

.scout-filter-container {
    width: 100%;
}

.scout-filter-wrapper {
    position: relative;
    display: inline-block;
    min-width: 268px;
}

.scout-filter-dropdown {
    padding: 8px 40px 8px 18px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #F9FAFB;
    font-size: 14px;
    color: #3c3c3c;
    cursor: pointer;
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: none;
}

.scout-filter-dropdown:focus {
    outline: none;
    border-color: #75C791;
}

.scout-filter-arrow {
    position: absolute;
    right: auto;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 18px;
    color: #3c3c3c;
}

.scout-filter-dropdown option {
    padding-left: 18px;
}

@media (max-width: 767px) {
    .scouted-tickets-section {
        margin-top: 0;
        margin-bottom: 8px;
    }

    .job-results-summary {
        margin-top: 25px;
    }

    .scout-filter-container {
        flex-direction: column;
        align-items: flex-start !important;
    }
    
    .scout-status-text {
        margin-left: 0;
        margin-top: 8px;
        order: -1;
    }
    
    .scout-filter-wrapper {
        min-width: 102px;
        width: 100%;
    }
    
    .scout-filter-dropdown {
        min-width: 102px;
    }
    
    .scout-filter-arrow {
        font-size: 16px;
    } 
    .toggle-container {
        width: 100%;
    }
    
    .search-row {
        height: 60px;
        width: 100% !important;
    }
    
    .search-icon {
        font-size: 20px;
        width: 20px;
    }

    .search-input {
        font-size: 14px;
        padding: 0 7px 0 40px;
    }

    .search-input::placeholder {
        font-family: 'Roboto', sans-serif;
        font-size: 12px;
        font-weight: 400;
        font-style: normal;
        line-height: 1;
        letter-spacing: 0;
    }
    
    .search-button {
        width: 100px;
        height: 40px;
        font-size: 14px;
        min-width: 100px;
    }
}

.scout-status-text {
    font-size: 12px;
    color: #3c3c3c;
    margin-left: 12px;
}

.scout-status {
    font-size: 12px;
    color: #3c3c3c;
    margin: 8px 16px 8px 16px;
    padding: 0;
}

.remaining-count {
    color: #ff0000;
    font-weight: bold;
}

.company-filter-container {
    width: 296px;
    padding: 4px 0px;
    height: 38px;
}

.company-filter-wrapper {
    position: relative;
    display: inline-block;
    width: 100%;
}

.company-filter-dropdown {
    padding: 8px 40px 8px 18px;
    border: 1px solid #ccc;
    border-radius: 4px;
    background-color: #F9FAFB;
    font-size: 14px;
    color: #3c3c3c;
    cursor: pointer;
    min-width: 180px;
    width: 100%;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: none;
}

.company-filter-dropdown:focus {
    outline: none;
    border-color: #75C791;
}

.company-filter-arrow {
    position: absolute;
    right: auto;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    font-size: 18px;
    color: #3c3c3c;
}

.company-filter-dropdown option {
    padding-left: 18px;
}

@media (max-width: 767px) {
    .company-filter-container {
        width: 100%;
    }
    .company-filter-wrapper {
        min-width: 102px;
        width: 100%;
    }
}
</style>
